import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createVNode as _createVNode, Fragment as _Fragment, createElementVNode as _createElementVNode, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "memo__modal__inner" }
const _hoisted_3 = { class: "memo__modal__title" }
const _hoisted_4 = ["placeholder", "value"]
const _hoisted_5 = { class: "memo__modal__info" }
const _hoisted_6 = { class: "memo__modal__info__length" }
const _hoisted_7 = { class: "memo__modal__control" }
const _hoisted_8 = { class: "memo__modal__control__inner" }

import TextButton from '@/components/input/Btn/TextButton.vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import { watch } from 'vue';
import { computed, ref, StyleValue } from 'vue';
import { useForms } from '../composables/useForm';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import InfoModal from '../components/InfoModal.vue';
import ErrorMessage from '../components/ErrorMessage.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'TableMemoButton',
  props: {
    memo: { default: undefined },
    modalCoordinateBase: { default: 'bottom-left' },
    needConfirmDeletion: { type: Boolean, default: true },
    needLabelText: { type: Boolean, default: false }
  },
  emits: ["save", "delete"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;

const props = __props;

const emits = __emit;

const fieldItems = computed((): FieldItem[] => {
  const items: FieldItem[] = [
    {
      name: 'memo',
      rules: [ValidateRule.MemoInvalidChar, ValidateRule.MemoTooMuchLength],
    },
  ];
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);

const modalState = ref(false);
const deleteModalState = ref(false);
const coordinateMemoButton = ref({
  x: 0,
  y: 0,
});
const isSaveable = ref(false);
const isEllipsis = ref(false);

fieldState['memo'].setValue(props.memo ?? '');

watch(
  () => props.memo,
  (newValue) => {
    resetForm(fieldItems.value);
    fieldState['memo'].setValue(newValue ?? '');
  }
);

/**
 * メモボタンをクリックした際の挙動
 * @param e - イベント
 */
const onClickMemoButton = (e: Event) => {
  const eventElement = e.target as HTMLElement;
  const parentElement = eventElement.closest('td') || eventElement.parentElement;
  if (!eventElement || !parentElement) return;

  const rect = parentElement.getBoundingClientRect();
  const offsetY = 20;
  const modalHeight = 278;
  const modalWidth = 500; // モーダルの幅を考慮
  const screenWidth = window.innerWidth;
  const edgePadding = 16; // 画面端からの余白
  const edgeWidth = screenWidth - edgePadding;

  // X座標の計算: modalCoordinateBase に応じて左上または右上を基準にする
  let coordX = props.modalCoordinateBase === 'bottom-right' ? rect.right + window.scrollX - modalWidth : rect.left + window.scrollX;

  // モーダルが画面の左端を超えないように調整
  if (coordX < edgePadding) {
    coordX = edgePadding;
  }

  // モーダルが画面の右端を超えないように調整
  if (coordX + modalWidth > edgeWidth) {
    coordX = edgeWidth - modalWidth;
  }

  // Y座標の計算: メモボタンの左下または右下に基準を合わせる
  const coordY =
    rect.top + window.scrollY + offsetY + modalHeight < window.scrollY + window.innerHeight
      ? rect.top + window.scrollY + offsetY
      : window.scrollY + window.innerHeight - modalHeight;

  coordinateMemoButton.value = { x: coordX, y: coordY };
  modalState.value = true;
};

const memoModalStyle = computed((): StyleValue => {
  return {
    top: `${coordinateMemoButton.value.y}px`,
    left: `${coordinateMemoButton.value.x}px`,
  };
});

/**
 * キャンセルボタンクリック時/バックグラウンドクリック時の処理
 */
const onClickClose = () => {
  modalState.value = false;
};

/**
 * 保存ボタンクリック時の処理
 */
const onClickSave = () => {
  emits('save', fieldState['memo'].value.value);
  modalState.value = false;
};

/**
 * 削除ボタンクリック時の処理
 */
const onClickDelete = () => {
  if (!props.needConfirmDeletion) {
    // 削除確認モーダルを表示しない場合は直で削除処理を実行
    applyDelete();
    modalState.value = false;
    return;
  }
  deleteModalState.value = true;
  modalState.value = false;
};

/**
 * 削除確認モーダルで削除を押した際の処理
 */
const applyDelete = () => {
  emits('delete');
  deleteModalState.value = false;
};

/**
 * 入力フォーム変更時の処理
 * @param e - イベント
 */
const onChangeInput = (e: Event) => {
  if (!e.target) return;
  const newValue = (e.target as HTMLTextAreaElement).value;
  fieldState['memo'].setValue(newValue ?? '');
  isSaveable.value = allValidate() && !!newValue.replace('\n', '').replace(/\s+/g, '');
};

const textFieldStyle = computed((): StyleValue => {
  return fieldState['memo'].isError.value ? { borderColor: '#d50000' } : { borderColor: '#e0e0e0' };
});

/**
 * テキストにマウスオーバーしたときに、テキストが省略されているかどうかを判定する
 * @param e - イベント
 */
const mouseoverTextLabel = (e: MouseEvent) => {
  const { target } = e;
  if (!(target instanceof HTMLElement)) return;
  isEllipsis.value = target.offsetWidth < target.scrollWidth;
};

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (props.memo)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "memo__text-button",
          style: _normalizeStyle({ fontSize: props.needLabelText ? '14px' : '12px' }),
          onClick: onClickMemoButton,
          onMouseover: mouseoverTextLabel
        }, [
          (props.needLabelText)
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(_unref(t)('メモ')) + "：", 1))
            : _createCommentVNode("", true),
          _createTextVNode(_toDisplayString(props.memo), 1)
        ], 36)), [
          [
            _directive_tooltip,
            isEllipsis.value ? props.memo : '',
            void 0,
            { bottom: true }
          ]
        ])
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (props.needLabelText)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "memo__button--with-label",
                style: _normalizeStyle({ fontSize: props.needLabelText ? '14px' : '12px' }),
                onClick: onClickMemoButton
              }, [
                _createTextVNode(_toDisplayString(_unref(t)('メモ')) + "：", 1),
                _createVNode(SvgLoader, { name: "addCircle" })
              ], 4))
            : (_openBlock(), _createElementBlock("button", {
                key: 1,
                class: "memo__button",
                onClick: onClickMemoButton
              }, [
                _createVNode(SvgLoader, { name: "addCircle" })
              ]))
        ], 64)),
    (modalState.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 2,
          to: "body"
        }, [
          _createElementVNode("div", {
            class: "memo__modal__bg",
            onClick: onClickClose
          }),
          _createElementVNode("div", {
            class: "memo__modal__container",
            style: _normalizeStyle(memoModalStyle.value)
          }, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, _toDisplayString(props.memo ? _unref(t)('メモ') : _unref(t)('メモの追加')), 1),
              (_openBlock(), _createElementBlock("textarea", {
                key: 'memo-input',
                class: "memo__modal__text-field",
                placeholder: _unref(t)('255文字以内で記入してください'),
                style: _normalizeStyle(textFieldStyle.value),
                value: _unref(fieldState)['memo'].value.value,
                onInput: onChangeInput
              }, null, 44, _hoisted_4)),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(ErrorMessage, {
                  "is-error": _unref(fieldState)['memo'].isError.value,
                  "error-message": _unref(fieldState)['memo'].errorMessage.value
                }, null, 8, ["is-error", "error-message"]),
                _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(fieldState)['memo'].value.value.length) + " / 255", 1)
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", null, [
                  (props.memo)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "memo__modal__control__delete",
                        onClick: onClickDelete
                      }, _toDisplayString(_unref(t)('このメモを削除')), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(TextButton, {
                    class: "memo__modal__control__button",
                    size: "btn-sm",
                    "button-style": "btn-secondary",
                    onClick: onClickClose
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('キャンセル')), 1)
                    ], undefined, true),
                    _: 1
                  }),
                  _createVNode(TextButton, {
                    class: "memo__modal__control__button",
                    size: "btn-sm",
                    "button-style": !isSaveable.value || _unref(fieldState)['memo'].isError.value ? 'btn-disabled' : 'btn-primary',
                    disabled: !isSaveable.value || _unref(fieldState)['memo'].isError.value,
                    onClick: onClickSave
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('保存')), 1)
                    ], undefined, true),
                    _: 1
                  }, 8, ["button-style", "disabled"])
                ])
              ])
            ])
          ], 4)
        ]))
      : _createCommentVNode("", true),
    (deleteModalState.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 3,
          to: "body"
        }, [
          _createVNode(InfoModal, {
            "modal-state": deleteModalState.value,
            icon: "error",
            "color-style": "error",
            "back-button-label": _unref(t)('キャンセル'),
            "next-button-label": _unref(t)('削除'),
            onOnBack: _cache[0] || (_cache[0] = 
        () => {
          deleteModalState.value = false;
          modalState.value = true;
          return;
        }
      ),
            onOnNext: _cache[1] || (_cache[1] = ($event: any) => (applyDelete()))
          }, {
            title: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('メモの削除')), 1)
            ]),
            description: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('一度削除すると復元することはできません。')), 1),
              _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1)),
              _createTextVNode(" " + _toDisplayString(_unref(t)('本当に削除しますか？')), 1)
            ]),
            _: 1
          }, 8, ["modal-state", "back-button-label", "next-button-label"])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})